import { AttachedFiles, type IHasAttachedFiles } from "legacyComponents/FileUploadContainer.types";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { type BuilderInfo } from "helpers/AppProvider.types";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import { type IHasCustomFieldContainer } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import {
    DiscussionsEntity,
    type IHasDiscussionContainer,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";

import {
    type BTFileSystem,
    type BTFileViewer,
    type DailyLogStatus,
    type DailyLogUserInfo,
    type DailyLogViewResponse,
    DocumentInstanceType,
    type Int32BTServiceListItem,
    type ReadReceiptsInfo,
    type RelatedDailyLogToDo,
    type TasksMappingData,
} from "handlers";

import { ViewableByData } from "entity/dailyLog/common/types/ViewableByData";
import { WeatherRecord } from "entity/dailyLog/common/types/WeatherRecord";
import { getLogTitle } from "entity/dailyLog/DailyLogView/DailyLogView.utils";
import { type IToDoCreationData } from "entity/toDo/details/ToDoDetails/ToDo.types";

export type DailyLogViewAction = "edit" | "print" | "delete" | undefined;

export enum DailyLogViewSections {
    none = "none",
    comments = "comments",
    documents = "documents",
    todos = "todos",
}

export interface IDailyLogViewState {
    jobName: string;
    logDate: moment.Moment;
    logTitle: string | undefined;
}

export class DailyLogViewEntity implements IHasAttachedFiles, IHasDiscussionContainer {
    constructor(builderInfo: BuilderInfo, data: DailyLogViewResponse) {
        this.id = data.id;
        this.jobId = data.jobId;
        this.builderId = data.builderId;
        this.jobName = data.jobName;
        this.logTitle = getLogTitle(
            builderInfo.locale.timeLocale,
            data.logTitle,
            moment(data.logDate),
        );
        this.logDate = moment(data.logDate);
        this.rawLogTitle = data.logTitle ?? "";
        this.notes = data.notes;
        this.files = data.files;
        this.images = data.images.files ?? [];
        this.weatherDetails = new WeatherDetails(data);
        this.instanceKey = uuidv4();
        this.discussions = new DiscussionsEntity(data.discussions);
        this.tags =
            data.tags.map(
                (t: Int32BTServiceListItem) => new BTSelectItem({ id: t.id, name: t.name }),
            ) ?? [];
        this.viewableBy = new ViewableByData(data.viewableBy);
        this.name = "Daily Log";
        this.documentInstanceType = DocumentInstanceType.DailyLog;
        this.attachedFiles = new AttachedFiles(data.files);
        this.userDetails = data.dailyLogUserInfo;
        this.relatedToDos = data.relatedToDos;
        this.canAddToDos = data.canAddToDos;
        this.tasksMappingData = data.tasksMappingData;
        this.customFieldData = {
            ...(data.customFields as unknown as IHasCustomFieldContainer),
        };
        this.isLocationTrackingEnabled = data.isLocationTrackingEnabled!;
        this.latitude = data.latitude!;
        this.longitude = data.longitude!;
        this.readReceipts = {
            seenByClients: data.readReceipts.seenByClients,
            seenByInternalUsers: data.readReceipts.seenByInternalUsers,
            seenBySubs: data.readReceipts.seenBySubs,
            earliestView: data.readReceipts.earliestView,
        };
        this.canEdit = data.canEdit;
        this.canDelete = data.canDelete;
        // `status` drives the View page Draft badge. When the autosave flag is off the
        // backend always reports Published, so the badge never renders.
        this.status = data.status;
    }

    id: number;
    jobId: number;
    builderId: number;
    jobName: string;
    logTitle: string;
    rawLogTitle: string;
    logDate: moment.Moment;
    notes: string;
    weatherDetails: WeatherDetails;
    files: BTFileViewer;
    images: BTFileSystem[];
    instanceKey: string;
    discussions: DiscussionsEntity;
    tags: BTSelectItem[];
    viewableBy: ViewableByData;
    name: string;
    documentInstanceType: DocumentInstanceType;
    attachedFiles: AttachedFiles;
    userDetails: DailyLogUserInfo;
    relatedToDos: RelatedDailyLogToDo[];
    canAddToDos: boolean;
    tasksMappingData: TasksMappingData;
    customFieldData: IHasCustomFieldContainer;
    isLocationTrackingEnabled: boolean;
    latitude: number | undefined;
    longitude: number | undefined;
    readReceipts: ReadReceiptsInfo;
    canEdit: boolean;
    canDelete: boolean;
    status: DailyLogStatus | undefined;
}

export class WeatherDetails {
    constructor(data: DailyLogViewResponse) {
        this.weatherRecord =
            data.weatherDetails.weatherInformation &&
            new WeatherRecord(data.weatherDetails.weatherInformation);
        this.weatherNotes = data.weatherDetails.weatherNotes;
    }

    weatherRecord: WeatherRecord | undefined;
    weatherNotes: string;
}

export interface IDailyLogViewPageProps {
    onBack: () => void;
    onEditClick: () => void;
    onPrintClick: () => void;
    onDeleteClick: () => void;
    onModalClose: () => Promise<void>;
    onUpdateCommentData: (comments: DiscussionsEntity) => void;
    dailyLog: DailyLogViewEntity;
    actionBeingPerformed: DailyLogViewAction;
    todoCreationData: IToDoCreationData | undefined;
    section: DailyLogViewSections;
}

export interface ITagsProps {
    tags: BTSelectItem[];
}

export class DailyLogViewDiscussionsEntity {
    constructor(discussions: DiscussionsEntity, id: number, jobId: number, builderId: number) {
        this.id = id;
        this.jobId = jobId;
        this.builderId = builderId;
        this.discussions = discussions;
        this.instanceKey = uuidv4();
    }

    id: number;
    jobId: number;
    builderId: number;
    discussions: DiscussionsEntity;
    instanceKey: string;
}

export type DailyLogViewDownloadAllActions = undefined | "download";

export interface IDailyLogViewPageSkeletonProps {
    onBack: () => void;
}

export interface IDailyLogRelatedTodosSkeletonProps {
    numberOfRows: number;
}

export interface IDailyLogNotesSkeletonsProps {
    screenSize: number;
    rowWidths: number[];
}
